import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Music, Mic2, Disc3, Headphones, Youtube, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import MediaKitNav from "@/components/MediaKitNav";
import MediaKitCarousel from "@/components/MediaKitCarousel";
import AnimatedCounter from "@/components/AnimatedCounter";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const FONT = "'Supersonic Rocketship', Impact, sans-serif";

const StrokeTitle = ({ children, as: Tag = "h2", className = "", stroke = 6 }) => (
  <Tag
    className={`font-bold text-center text-black ${className}`}
    style={{ fontFamily: FONT, WebkitTextStroke: `${stroke}px white`, paintOrder: "stroke fill" }}
  >
    {children}
  </Tag>
);

const BigStat = ({ icon: Icon, color, value, label, testId, children }) => (
  <div className="text-center" data-testid={testId}>
    <Icon className={`w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 ${color}`} />
    <div className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2" style={{ fontFamily: FONT }}>
      {children ?? <AnimatedCounter value={value} />}
    </div>
    <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">{label}</p>
  </div>
);

export default function MediaKitMusicaPage() {
  const [stats, setStats] = useState(null);
  const [musicStats, setMusicStats] = useState(null);
  const [concertPhotos, setConcertPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, musicRes, photosRes, albumsRes] = await Promise.all([
        axios.get(`${API}/media-kit/stats`),
        axios.get(`${API}/media-kit/music`).catch(() => ({ data: {} })),
        axios.get(`${API}/music/concert-photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/music-thumbnails`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setMusicStats(musicRes.data);
      setConcertPhotos(photosRes.data.filter((p) => p.active));
      setAlbums(albumsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img
          src="https://customer-assets-0z36b82j.emergentagent.net/job_6374765b-0927-4fa7-ace6-9a95eb25e587/artifacts/6cbu7wu9_LOADING.gif"
          alt="Cargando..."
          className="w-48 h-auto"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col text-white">
      <Navbar />

      {/* ===== HEADER - White with opacity ===== */}
      <section className="relative pt-44 pb-12" data-testid="music-mk-header">
        <div className="absolute inset-0 bg-white/40" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="text-center mb-8">
            <StrokeTitle as="h1" stroke={8} className="text-5xl md:text-7xl mb-4">MÚSICA</StrokeTitle>
            <p className="text-lg md:text-xl text-[#FFE500]" style={{ fontFamily: FONT }}>
              Vete a la Versh, el musical
            </p>
          </div>
          <MediaKitNav />
        </div>
      </section>

      {/* ===== STATS - Black with opacity ===== */}
      <section className="relative py-16">
        <div className="absolute inset-0 bg-black/70" />
        <div className="max-w-6xl mx-auto px-4 relative z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          <BigStat icon={Headphones} color="text-green-500" value={musicStats?.spotify_monthly_listeners || 0} label="Oyentes mensuales" testId="stat-spotify" />
          <BigStat icon={Disc3} color="text-green-500" value={musicStats?.spotify_monthly_streams || 0} label="Reproducciones mensuales" testId="stat-spotify-monthly-streams" />
          <BigStat icon={Music} color="text-green-500" value={musicStats?.spotify_total_streams || 0} label="Streams totales" testId="stat-spotify-total-streams" />
          <BigStat icon={Users} color="text-green-500" value={musicStats?.spotify_followers || 0} label="Seguidores Spotify" testId="stat-spotify-followers" />
          <div className="col-span-2 md:col-span-1">
            <BigStat icon={Youtube} color="text-red-500" value={stats?.youtube?.subscribers || 0} label="Suscriptores YouTube" testId="stat-youtube-music" />
          </div>
        </div>
      </section>

      {/* ===== CONCERT PHOTOS - White with opacity ===== */}
      {concertPhotos.length > 0 && (
        <section className="relative py-16" data-testid="concert-photos-section">
          <div className="absolute inset-0 bg-white/40" />
          <div className="relative z-10 max-w-6xl mx-auto px-4">
            <StrokeTitle className="text-3xl md:text-4xl mb-12">EN VIVO</StrokeTitle>
          </div>
          <MediaKitCarousel testId="concert-carousel">
            {concertPhotos.map((photo) => (
              <div
                key={photo.id}
                className="flex-shrink-0 w-[300px] md:w-[440px] bg-[#141414] border border-white/10 rounded-2xl overflow-hidden"
                data-testid={`concert-photo-${photo.id}`}
              >
                <div className="aspect-[4/3] bg-black">
                  <img src={photo.image_url} alt={photo.caption || "Concierto"} className="w-full h-full object-cover" />
                </div>
                {photo.caption && <p className="p-4 text-gray-300 text-sm">{photo.caption}</p>}
              </div>
            ))}
          </MediaKitCarousel>
        </section>
      )}

      {/* ===== ALBUMS - Black with opacity ===== */}
      {albums.length > 0 && (
        <section className="relative py-16" data-testid="albums-section">
          <div className="absolute inset-0 bg-black/70" />
          <h2 className="relative z-10 text-3xl md:text-4xl font-bold text-center mb-12 px-4" style={{ fontFamily: FONT }}>
            <span className="text-[#FFE500]">ÁLBUMES</span> <span className="text-white">DESTACADOS</span>
          </h2>
          <MediaKitCarousel testId="albums-carousel">
            {albums.map((album) => (
              <a
                key={album.id}
                href={album.link_url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex-shrink-0 w-[280px] md:w-[320px] bg-[#141414] border border-white/10 rounded-2xl overflow-hidden"
                data-testid={`album-${album.id}`}
              >
                <div className="aspect-square bg-black overflow-hidden">
                  {album.image_url ? (
                    <img src={album.image_url} alt={album.title || "Álbum"} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Music className="w-16 h-16 text-white/30" /></div>
                  )}
                </div>
                {album.title && (
                  <div className="p-4">
                    <h3 className="font-bold text-white group-hover:text-[#E91E8C] transition-colors">{album.title}</h3>
                  </div>
                )}
              </a>
            ))}
          </MediaKitCarousel>
        </section>
      )}

      {/* ===== CTA - White with opacity ===== */}
      <section className="relative py-20">
        <div className="absolute inset-0 bg-white/40" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <StrokeTitle className="text-3xl md:text-4xl mb-6">BOOKING</StrokeTitle>
          <p className="text-xl text-black font-semibold mb-8">
            ¿Tienes un evento próximo? Hagamos algo memorable juntos.
          </p>
          <Link to="/contacto">
            <Button
              className="bg-black hover:bg-black/80 text-white text-lg px-10 py-6 font-black hand-drawn-subtle border-[3px] border-white"
              style={{ fontFamily: FONT }}
              data-testid="cta-contact"
            >
              <Mic2 className="mr-2" />
              SOLICITAR COTIZACIÓN
            </Button>
          </Link>
        </div>
      </section>

      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
